import { Engineering } from '@mui/icons-material'
import { Box, Grid } from '@mui/material'
import { GridColDef as GridEnrichedColDef } from '@mui/x-data-grid-premium'
import { format } from 'date-fns'
import distanceInWords from 'date-fns/formatDistance'
import { Fragment } from 'react'
import { User, UserContractor } from 'src/api'
import { ClosedEye, Crown } from 'src/components/icons'
import { InnerLink } from 'src/components/ui'
import { capitalizeFirstLetter } from 'src/utils/formatters'

export namespace UserColumnsDataGrid {
  export const NameWithIcon: (pmRoleId: number | undefined, ownerRoleId: number | undefined) => GridEnrichedColDef<User> = (pmRoleId, ownerRoleId) => ({
    field: 'name',
    headerName: 'Name',
    type: 'string',
    minWidth: 250,
    editable: false,
    cellProps: { align: 'left' },
    valueGetter: (params) => params.row?.name || null,
    renderCell: ({ row }) => {
      const isPM = pmRoleId === row.roleId
      const isOwner = ownerRoleId === row.roleId
      const hasUserContractors = row.userContractors && row.userContractors.length > 0
      const hasAtLeastOneActiveContractor = hasUserContractors && row.userContractors?.some((uc) => !uc.isArchived)
      const isArchived = hasUserContractors ? !hasAtLeastOneActiveContractor : row.isArchived
      const otherUserContractorsNames = Array.from(
        new Set((row.userContractors || []).map((uc) => uc.name).filter((name): name is string => Boolean(name) && name !== row.name))
      )

      return (
        <Grid container alignItems="center" spacing={1} flexWrap="nowrap">
          <Grid item>
            <InnerLink to={`/users/${row.id}`}>
              {row.name || '-'}
              {otherUserContractorsNames.length > 0 && ` (${otherUserContractorsNames.join(', ')})`}
            </InnerLink>
          </Grid>
          {isArchived && (
            <Grid item display="flex" title="Archived User">
              <ClosedEye />
            </Grid>
          )}
          {isPM && (
            <Grid item display="flex" title="Project Manager">
              <Engineering fontSize="small" />
            </Grid>
          )}
          {isOwner && (
            <Grid item display="flex" title="Owner">
              <Crown />
            </Grid>
          )}
        </Grid>
      )
    }
  })

  export const Email: GridEnrichedColDef<User> = {
    field: 'email',
    headerName: 'Email',
    type: 'string',
    minWidth: 250,
    editable: false
  }
  export const Phone: GridEnrichedColDef<User> = {
    field: 'phone',
    headerName: 'Phone',
    type: 'string',
    minWidth: 180,
    editable: false
  }
  export const LastActive: GridEnrichedColDef<User> = {
    field: 'lastActive',
    headerName: 'Last Active',
    type: 'string',
    minWidth: 180,
    editable: false,
    valueFormatter: ({ value }) => (value ? distanceInWords(new Date(value), new Date()) : '-')
  }
  export const ContractorName: GridEnrichedColDef<User> = {
    field: 'contractor.name',
    headerName: 'Contractor',
    type: 'string',
    minWidth: 250,
    editable: false,
    valueGetter: ({ row }) => {
      if (row?.userContractors && row?.userContractors.length > 0) {
        return row.userContractors.map(({ contractor }) => contractor?.name).join(',')
      }

      return row.contractor?.name || null
    },
    renderCell: ({ row }) => {
      if (row?.userContractors && row?.userContractors.length > 0) {
        const usersContractors: UserContractor[] = row?.userContractors || []
        return (
          <Box>
            {usersContractors.map(({ contractorId, contractor }, idx) => (
              <Fragment key={contractorId}>
                <InnerLink to={`/contractors/${contractorId}`}>{contractor?.name || '-'}</InnerLink>
                {idx !== usersContractors.length - 1 && <br />}
              </Fragment>
            ))}
          </Box>
        )
      }

      if (row?.contractor) {
        const contractorId = row?.contractor.id
        const contractorName = row?.contractor.name
        return <InnerLink to={`/contractors/${contractorId}`}>{contractorName}</InnerLink>
      }
      return null
    }
  }
  export const LastAppVersion: GridEnrichedColDef<User> = {
    field: 'lastVersionApp',
    headerName: 'Mobile App Version',
    type: 'string',
    minWidth: 180,
    editable: false,
    valueFormatter: ({ value }) => value || '-'
  }
  export const CreatedAt: GridEnrichedColDef<User> = {
    field: 'createdAt',
    headerName: 'Created At',
    type: 'string',
    minWidth: 200,
    editable: false,
    valueFormatter: ({ value }) => (value ? format(new Date(value), 'PPpp') : '-')
  }
  export const UserRole: GridEnrichedColDef<User> = {
    field: 'role.displayName',
    headerName: 'Role',
    type: 'string',
    minWidth: 150,
    editable: false,
    valueGetter: ({ row }) => {
      if (row?.userContractors && row?.userContractors.length > 0) {
        const usersContractors: UserContractor[] = row?.userContractors || []
        const uniqueRoles = new Set(usersContractors.map(({ role }) => role?.displayName))
        return Array.from(uniqueRoles).join(',')
      }

      return row.role?.displayName || null
    },
    renderCell: ({ row }) => {
      if (row?.userContractors && row?.userContractors.length > 0) {
        const usersContractors: UserContractor[] = row?.userContractors || []
        const uniqueRoles = Array.from(new Set(usersContractors.map(({ role }) => role?.displayName)))
        return (
          <Box>
            {uniqueRoles.map((role, idx) => (
              <Fragment key={role}>
                {role}
                {idx !== uniqueRoles.length - 1 && <br />}
              </Fragment>
            ))}
          </Box>
        )
      }

      return row.role?.displayName || null
    }
  }
  export const UserStatus: GridEnrichedColDef<User> = {
    field: 'isArchived',
    headerName: 'Status',
    type: 'string',
    minWidth: 150,
    editable: false,
    valueGetter: ({ row }) => {
      if (row?.userContractors && row?.userContractors.length > 0) {
        const hasAtLeastOneActiveContractor = row.userContractors.some(({ isArchived }) => !isArchived)
        return hasAtLeastOneActiveContractor ? 'Active' : 'Archived'
      }

      return row.isArchived ? 'Archived' : 'Active'
    }
  }
  export const MessageChannel: GridEnrichedColDef<User> = {
    field: 'messageChannel',
    headerName: 'Message channel',
    type: 'string',
    minWidth: 150,
    editable: false,
    valueFormatter: ({ value }) => capitalizeFirstLetter(value)
  }
}
